// src/components/map/hooks/adapters/useMapAttentionOverlay.ts
//
// Adapter hook: Attention Overlay
//
// Owns:
//   - ATTENTION_WELL_LAYER source data updates — the union of two highlight
//     channels: attentionWells (ephemeral chart hover) and jumpHighlightWells
//     (durable Jump to Location highlight)
//   - ATTENTION_POLYGON_LAYER source + layer creation and updates
//   - clearHoverHighlighting / clearHoverHighlightingDebounced (clearing
//     attentionWells ONLY — never jumpHighlightWells)
//
// Mapbox layers/sources managed:
//   - ATTENTION_WELL_LAYER (source data updates + line-width paint property)
//   - ATTENTION_WELL_JUMP_LAYER / ATTENTION_WELL_DOT_LAYER and their glow
//     layers (jump wells only, never chart hover)
//   - ATTENTION_POLYGON_LAYER (source + layer creation and data updates)
//
// Store reads: selectAttentionWells, selectJumpHighlightWells, selectAttentionPolygon
//   (map), the card highlight's visibility and the well card's outline uwi
//
// This is the lowest-coupling hook in the decomposition — it reads from the
// Zustand store and writes to the map, with no cross-hook dependencies except
// the functions it receives as parameters.
import { MutableRefObject, useCallback, useEffect, useMemo, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";

import { feature, featureCollection } from "@turf/helpers";
import {
  ATTENTION_POLYGON_LAYER,
  ATTENTION_WELL_DOT_GLOW_LAYER,
  ATTENTION_WELL_DOT_LAYER,
  ATTENTION_WELL_JUMP_GLOW_LAYER,
  ATTENTION_WELL_JUMP_LAYER,
  ATTENTION_WELL_LAYER,
  ATTENTION_WELL_POINT_LAYER,
  FACILITY_LAYER,
  HZ_RASTER_MVT_CROSSOVER_ZOOM,
  SELECTED_SINGLE_WELL_POINT_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT,
  WELL_SOURCE_IDS,
  highlightDotRadius,
  highlightGlowRadius,
  highlightLineGlowPaint,
  vertWellCircleRadius
} from "constants/mapLayers.constants";
import mapboxgl, { GeoJSONSource } from "mapbox-gl";
import {
  selectAttentionPolygon,
  selectAttentionWells,
  selectDisplaySettings,
  selectJumpHighlightWells,
  selectLayers,
  useMapStore
} from "stores/map";
import { selectWellCardOutlineUwi, useWellCardPanelStore } from "stores/wellCardPanel";
import { useDebouncedCallback } from "use-debounce";

import { emptyFeatureCollection } from "../../utils/emptyFeatureCollection";
import { keepMissingFeatures } from "../../utils/keepMissingFeatures";

// ── Types ────────────────────────────────────────────────────────────────────

export interface UseMapAttentionOverlayParams {
  readonly mapboxRef: MutableRefObject<Maybe<mapboxgl.Map>>;
  readonly addLayer: (layer: mapboxgl.AnyLayer, before?: string) => void;
  readonly getHighlightedLineThickness: () => number;
  readonly highlightFacilities: (facilities: mapboxgl.MapboxGeoJSONFeature[]) => void;
  readonly clearHighlightedFacilities: () => void;
  /**
   * Reactive readiness signal — true once useMapLifecycle has built the map.
   * mapboxRef is a ref OBJECT, so an effect keyed on it never re-runs when
   * `.current` is assigned; this hook runs before useMapLifecycle, so effects
   * gated on the ref alone see null and bail forever.
   */
  readonly isMapInitialized?: boolean;
  /** Sync has turned map highlighting off, which keeps the card's highlight off too. */
  readonly isSyncHighlightOff?: boolean;
}

export interface UseMapAttentionOverlayReturn {
  /** Immediately clears attention wells in the store. */
  readonly clearHoverHighlighting: () => void;
  /** Debounced (100 ms) version of clearHoverHighlighting to prevent flickering during zoom/pan. */
  readonly clearHoverHighlightingDebounced: ReturnType<typeof useDebouncedCallback>;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useMapAttentionOverlay({
  mapboxRef,
  addLayer,
  getHighlightedLineThickness,
  highlightFacilities,
  clearHighlightedFacilities,
  isMapInitialized,
  isSyncHighlightOff
}: UseMapAttentionOverlayParams): UseMapAttentionOverlayReturn {
  // ── Store selectors ────────────────────────────────────────────────────
  const attentionWells = useMapStore(selectAttentionWells);
  // Durable Jump to Location highlight — separate from attentionWells, see
  // mapTypes.ts's jumpHighlightWells JSDoc.
  const jumpHighlightWells = useMapStore(selectJumpHighlightWells);
  const attentionPolygon = useMapStore(selectAttentionPolygon);
  // Sizes the vertical glyph, so it has to size the marker that sits on it.
  const displaySettings = useMapStore(selectDisplaySettings);
  const setAttentionWells = useMapStore((s) => s.setAttentionWells);
  const setJumpHighlightWells = useMapStore((s) => s.setJumpHighlightWells);

  // Both channels render on the same ATTENTION_WELL_LAYER. Re-derived only on a
  // content change — both setters are structural-equality guarded upstream.
  const highlightWells = useMemo(
    () => Array.from(new Set([...attentionWells, ...jumpHighlightWells])),
    [attentionWells, jumpHighlightWells]
  );
  // The well the card highlight is on, the same one MapboxWrapper outlines.
  const cardUwi = useWellCardPanelStore(selectWellCardOutlineUwi);
  // Every card highlight layer follows Selected Well, so the dot stands for all.
  const isCardHighlightVisible = useMapStore(
    (s) =>
      selectLayers(s).find((layer) => layer.id === SELECTED_SINGLE_WELL_POINT_LAYER)
        ?.isVisible !== false
  );
  const cardHighlightUwi = isCardHighlightVisible && !isSyncHighlightOff ? cardUwi : null;
  // The glow marks a jump only; a chart hover keeps its plain black look. The
  // card already glows its own well, and a second glow on it would double up.
  // When the card's highlight is hidden, the jump keeps its own.
  const jumpWellSet = useMemo(() => {
    const wells = new Set(jumpHighlightWells);
    wells.delete(cardHighlightUwi);
    return wells;
  }, [jumpHighlightWells, cardHighlightUwi]);

  // ── clearHoverHighlighting ─────────────────────────────────────────────
  // Clears ONLY the ephemeral hover channel. Must never touch
  // jumpHighlightWells, which has to survive the camera move a jump triggers.
  const clearHoverHighlighting = useCallback(() => {
    setAttentionWells([]);
  }, [setAttentionWells]);

  // Debounced version to avoid flickering during zoom/pan
  const clearHoverHighlightingDebounced = useDebouncedCallback(
    clearHoverHighlighting,
    100 // 100ms delay - fast enough to feel responsive, slow enough to prevent flickering
  );

  // ── Attention polygon layer lifecycle ──────────────────────────────────
  useEffect(() => {
    function showAttentionPolygon(polygon: GeoJSON.Polygon | GeoJSON.MultiPolygon) {
      const mapbox = mapboxRef.current;
      if (!mapbox) {
        return;
      }
      if (!mapbox.getSource(ATTENTION_POLYGON_LAYER)) {
        mapbox.addSource(ATTENTION_POLYGON_LAYER, {
          data: { ...emptyFeatureCollection },
          type: "geojson"
        });
      }
      if (!mapbox.getLayer(ATTENTION_POLYGON_LAYER)) {
        addLayer({
          id: ATTENTION_POLYGON_LAYER,
          source: ATTENTION_POLYGON_LAYER,
          type: "line",
          paint: {
            "line-color": "black",
            "line-width": 4
          }
        });
      }
      const data = { ...emptyFeatureCollection };
      if (polygon) {
        data.features = [feature(polygon)];
      }
      (mapbox.getSource(ATTENTION_POLYGON_LAYER) as GeoJSONSource).setData(data);
    }

    showAttentionPolygon(attentionPolygon);
  }, [addLayer, attentionPolygon, mapboxRef]);

  // What each branch was last applied to (sorted + joined ids), so a re-apply
  // resolving the same features can skip the work. The facility branch
  // otherwise churned removeLayer/addLayer on every `idle`, flickering during a
  // pan. The well branch needs it for a different reason: setData is not a
  // no-op on identical data — it marks the source unloaded and repaints, whose
  // next frame fires `idle` again, so with a DURABLE highlight this handler
  // feeds itself forever (mapbox-gl 2.15, geojson_source _updateWorkerData).
  const lastAppliedWellIdsRef = useRef<Nullable<string>>(null);
  const lastAppliedVertWellIdsRef = useRef<Nullable<string>>(null);
  const lastAppliedJumpLineIdsRef = useRef<Nullable<string>>(null);
  const lastAppliedDotIdsRef = useRef<Nullable<string>>(null);
  const lastJumpLinesRef = useRef<GeoJSON.Feature[]>([]);
  const lastJumpDotsRef = useRef<GeoJSON.Feature[]>([]);
  const lastAppliedFacilityIdsRef = useRef<Nullable<string>>(null);

  // ── Highlight source updates (chart hover + Jump to Location → map) ────
  const applyAttentionWells = useCallback(() => {
    // Removed useEffect with dependency on attentionWells. Highlighting was not working sporadically,
    // or after switching projects.
    if (!mapboxRef?.current || !highlightWells) return;

    const mapbox = mapboxRef.current;

    const attentionSource: mapboxgl.GeoJSONSource =
      mapbox.getSource(ATTENTION_WELL_LAYER);
    const attentionPointSource = mapbox.getSource(
      ATTENTION_WELL_POINT_LAYER
    ) as mapboxgl.GeoJSONSource;
    const jumpLineSource = mapbox.getSource(
      ATTENTION_WELL_JUMP_LAYER
    ) as mapboxgl.GeoJSONSource;
    const dotSource = mapbox.getSource(
      ATTENTION_WELL_DOT_LAYER
    ) as mapboxgl.GeoJSONSource;

    if (attentionSource?.setData) {
      if (highlightWells.length) {
        // Each well source separately, and onto its own overlay source.
        //
        // querySourceFeatures takes a SOURCE id and does not apply layer
        // filters, so the reason verticals were missing is not a filter — it is
        // that they live in a separate source entirely (vert.mvt against
        // hz.mvt), registered under WELL_LAYER_POINT by addLayer's
        // inline-source form. Asking the horizontals' source could never have
        // returned them. They are kept apart downstream too, the way
        // useContributionHighlight does it: a circle layer draws at every
        // vertex it is given, so pouring the lines into the circle layer's
        // source drew black dots along every horizontal well.
        //
        // Sticks need no entry here: STICK_LAYER declares `source: WELL_LAYER`,
        // so there is no stick source and its features already come back from
        // "Wells" precisely because layer filters do not apply.
        const queryWellSource = (sourceId: string) =>
          // Guarded because a source is only present once the map has added
          // it, and querySourceFeatures on a missing source throws — which
          // would take the whole highlight down rather than one kind of well.
          mapbox.getSource(sourceId)
            ? mapbox.querySourceFeatures(sourceId, {
                sourceLayer: "eva-wells",
                // `match` not `in`: In does an O(n) indexOf per rendered
                // feature, and a jump can set 5000 wells, re-scanned on every
                // idle for as long as the durable highlight lives. Match
                // compiles to an O(1) lookup.
                filter: ["match", ["get", "Uwi"], highlightWells, true, false]
              })
            : [];

        const wells = queryWellSource(WELL_LAYER);
        // A multilateral's legs ride in the vertical tiles too, as lines.
        const vertWells = queryWellSource(WELL_LAYER_POINT).filter(
          (feat) => feat.geometry.type === "Point"
        );

        const highlightedLineThickness = getHighlightedLineThickness();
        if (mapbox.getLayer(ATTENTION_WELL_LAYER)) {
          mapbox.setPaintProperty(
            ATTENTION_WELL_LAYER,
            "line-width",
            highlightedLineThickness
          );
        }
        if (mapbox.getLayer(ATTENTION_WELL_JUMP_LAYER)) {
          mapbox.setPaintProperty(
            ATTENTION_WELL_JUMP_LAYER,
            "line-width",
            highlightedLineThickness
          );
        }
        if (mapbox.getLayer(ATTENTION_WELL_JUMP_GLOW_LAYER)) {
          const glow = highlightLineGlowPaint(highlightedLineThickness);
          mapbox.setPaintProperty(
            ATTENTION_WELL_JUMP_GLOW_LAYER,
            "line-width",
            glow["line-width"]
          );
          mapbox.setPaintProperty(
            ATTENTION_WELL_JUMP_GLOW_LAYER,
            "line-blur",
            glow["line-blur"]
          );
        }
        if (mapbox.getLayer(ATTENTION_WELL_POINT_LAYER)) {
          // The glyph's own size, off the same wellDotThickness setting that
          // sizes it. Pinning this to the thickness-0 ramp instead put the
          // marker behind the very well it marks as soon as the dot size was
          // raised.
          mapbox.setPaintProperty(
            ATTENTION_WELL_POINT_LAYER,
            "circle-radius",
            vertWellCircleRadius(displaySettings.wellDotThickness)
          );
        }
        if (mapbox.getLayer(ATTENTION_WELL_DOT_LAYER)) {
          mapbox.setPaintProperty(
            ATTENTION_WELL_DOT_LAYER,
            "circle-radius",
            highlightDotRadius(displaySettings.wellDotThickness)
          );
        }
        if (mapbox.getLayer(ATTENTION_WELL_DOT_GLOW_LAYER)) {
          mapbox.setPaintProperty(
            ATTENTION_WELL_DOT_GLOW_LAYER,
            "circle-radius",
            highlightGlowRadius(displaySettings.wellDotThickness)
          );
        }

        // Same memo-and-clear behaviour for both kinds of well, one function
        // rather than two copies.
        const applyWells = (
          source: mapboxgl.GeoJSONSource,
          features: Pick<GeoJSON.Feature, "geometry" | "properties">[],
          lastIdsRef: MutableRefObject<Nullable<string>>,
          featureKey = (f: Pick<GeoJSON.Feature, "geometry" | "properties">) =>
            String(f.properties?.Uwi ?? "")
        ) => {
          if (!source?.setData) return;

          if (features.length > 0) {
            const resolvedIds = features.map(featureKey).sort().join(",");
            if (resolvedIds !== lastIdsRef.current) {
              source.setData(
                featureCollection(features.map((feat) => feature(feat.geometry)))
              );
              lastIdsRef.current = resolvedIds;
            }
            return;
          }

          // Nothing resolved for the CURRENT set, so whatever the layer still
          // draws belongs to a previous one. This branch was missing while
          // attentionWells was wiped on every `move`; with a durable highlight,
          // jumping to a well the active filter excludes flew the camera to B
          // while still drawing A at A's location — a wrong marker, not a
          // missing one. "" is a sentinel distinct from the initial null, so the
          // clear happens once rather than on every idle.
          if (lastIdsRef.current !== "") {
            source.setData(emptyFeatureCollection);
            lastIdsRef.current = "";
          }
        };

        applyWells(attentionSource, wells, lastAppliedWellIdsRef);
        applyWells(attentionPointSource, vertWells, lastAppliedVertWellIdsRef);
        // A jumped well is drawn again on its own black core and glow, so the
        // glow never reaches a hovered well and both draw above the well lines.
        const isJumpWell = (f: mapboxgl.MapboxGeoJSONFeature) =>
          jumpWellSet.has(String(f.properties?.Uwi));
        const toJumpFeature = (f: mapboxgl.MapboxGeoJSONFeature) =>
          feature(f.geometry, { Uwi: String(f.properties?.Uwi) });
        // Both well sources stop at the crossover. Above it, a well with no
        // features was filtered out, so its old highlight would mark nothing.
        const isBelowCrossover = mapbox.getZoom() < HZ_RASTER_MVT_CROSSOVER_ZOOM;
        const jumpLines = keepMissingFeatures(
          wells.filter(isJumpWell).map(toJumpFeature),
          jumpWellSet,
          isBelowCrossover ? lastJumpLinesRef.current : []
        );
        const jumpDots = keepMissingFeatures(
          vertWells.filter(isJumpWell).map(toJumpFeature),
          jumpWellSet,
          isBelowCrossover ? lastJumpDotsRef.current : []
        );
        lastJumpLinesRef.current = jumpLines;
        lastJumpDotsRef.current = jumpDots;
        // Keyed on geometry: more of a well's tiles load, or a pan swaps in
        // other clipped pieces, with the same Uwi set.
        const geometryKey = (f: Pick<GeoJSON.Feature, "geometry" | "properties">) =>
          `${f.properties?.Uwi}@${JSON.stringify(
            (f.geometry as GeoJSON.LineString | GeoJSON.Point).coordinates
          )}`;
        applyWells(jumpLineSource, jumpLines, lastAppliedJumpLineIdsRef, geometryKey);
        applyWells(dotSource, jumpDots, lastAppliedDotIdsRef, geometryKey);

        const facilities = mapbox.querySourceFeatures(FACILITY_LAYER, {
          sourceLayer: "eva-facility",
          // `match` not `in`: In does an O(n) indexOf per rendered feature, and
          // a jump can set 5000 wells, re-scanned on every idle for as long as
          // the durable highlight lives. Match compiles to an O(1) lookup.
          filter: ["match", ["get", "Uwi"], highlightWells, true, false]
        });

        if (facilities.length > 0) {
          const uniqueFacilities = facilities.reduce((acc, facility) => {
            const uwi = facility.properties?.Uwi;
            if (uwi && !acc.find((f) => f.properties?.Uwi === uwi)) {
              acc.push(facility);
            }
            return acc;
          }, [] as mapboxgl.MapboxGeoJSONFeature[]);

          const resolvedFacilityIds = uniqueFacilities
            .map((f) => String(f.properties?.Uwi ?? ""))
            .sort()
            .join(",");
          if (resolvedFacilityIds !== lastAppliedFacilityIdsRef.current) {
            highlightFacilities(uniqueFacilities);
            lastAppliedFacilityIdsRef.current = resolvedFacilityIds;
          }
        } else if (lastAppliedFacilityIdsRef.current !== "") {
          // "" is a sentinel distinct from the initial `null`, so the first
          // apply still clears once while later identical idles are skipped.
          clearHighlightedFacilities();
          lastAppliedFacilityIdsRef.current = "";
        }
      } else {
        attentionSource.setData(emptyFeatureCollection);
        attentionPointSource?.setData?.(emptyFeatureCollection);
        jumpLineSource?.setData?.(emptyFeatureCollection);
        dotSource?.setData?.(emptyFeatureCollection);
        clearHighlightedFacilities();
        lastAppliedWellIdsRef.current = null;
        lastAppliedVertWellIdsRef.current = null;
        lastAppliedJumpLineIdsRef.current = null;
        lastAppliedDotIdsRef.current = null;
        lastJumpLinesRef.current = [];
        lastJumpDotsRef.current = [];
        lastAppliedFacilityIdsRef.current = null;
      }
    }
  }, [
    highlightWells,
    jumpWellSet,
    mapboxRef,
    highlightFacilities,
    clearHighlightedFacilities,
    getHighlightedLineThickness,
    displaySettings.wellDotThickness
  ]);

  useEffect(() => {
    applyAttentionWells();
  }, [applyAttentionWells]);

  // The jump highlight survives the jump's OWN camera move, then clears on the
  // user's next interaction. `originalEvent` is what separates the two: a
  // gesture carries the DOM event that caused it, while a camera method like
  // fitBounds fires movestart without one — clearing on every move is what made
  // this channel necessary at all, since the fly-to wiped its own highlight.
  //
  // Registered only while a jump highlight is set, so ordinary map use costs
  // nothing.
  //
  // Known gap, not fixable here: a well the active filter's PROPERTY predicates
  // exclude is absent from WELL_LAYER's tiles entirely, and only WELL_LAYER
  // (horizontals) is queried — so neither channel can highlight such a well.
  useEffect(() => {
    const mapbox = mapboxRef?.current;
    if (!mapbox || !isMapInitialized || jumpHighlightWells.length === 0) {
      return undefined;
    }

    // Panning and zooming do NOT clear it. The marker is how the user finds
    // the wells they jumped to, so it has to survive the zooming-in and
    // panning-around that looking at them involves — matching Insights, where
    // the marker persists through navigation. Only a click or Escape (below)
    // clears it: that is the user turning their attention to something else.
    function clearOnMapClick(event?: { originalEvent?: unknown }) {
      // Defensive only. This guard was load-bearing while the handler was on
      // `movestart`, where the jump's own fitBounds fires without an
      // originalEvent; mapbox-gl's camera methods fire movestart/moveend and
      // never `click`, so nothing reaching here today lacks one.
      if (!event?.originalEvent) return;
      setJumpHighlightWells([]);
    }

    mapbox.on("click", clearOnMapClick);
    return () => {
      mapbox.off("click", clearOnMapClick);
    };
  }, [mapboxRef, isMapInitialized, jumpHighlightWells.length, setJumpHighlightWells]);

  // Escape clears it too, on the same press that clears the selection
  // (useMapClickSelection). The setter skips an unchanged set.
  useHotkeys("esc", () => setJumpHighlightWells([]));

  // isMapInitialized is in the deps, not just mapboxRef: the ref object never
  // changes, so an effect keyed on it alone runs once against a null map and
  // never re-runs. highlightWells.length is not enough either — on a
  // MapboxWrapper remount with a durable highlight already set, the store value
  // does not change, so nothing would re-register these listeners.
  //
  // `idle` also picks up tiles that load after a fly-to has already run this
  // apply against an empty source.
  useEffect(() => {
    const mapbox = mapboxRef?.current;
    if (!mapbox || !highlightWells.length) return;

    // A style reload (base-layer toggle -> setStyle) destroys every source and
    // layer, so without dropping the memos the next idle resolves the same ids,
    // skips re-applying, and strands the highlight until the set changes.
    const invalidate = () => {
      lastAppliedWellIdsRef.current = null;
      lastAppliedVertWellIdsRef.current = null;
      lastAppliedJumpLineIdsRef.current = null;
      lastAppliedDotIdsRef.current = null;
      lastAppliedFacilityIdsRef.current = null;
    };

    // querySourceFeatures only sees loaded tiles, and a jump flies to wells that
    // are by definition off screen, so the first apply always resolves nothing.
    // The tiles arrive twice over (new viewport, then the map-view refilter's
    // new filterId), and neither is a style reload — so the memo has to be
    // dropped here too, or that empty apply counts as already-applied.
    const onWellTiles = (event?: { sourceId?: string; isSourceLoaded?: boolean }) => {
      // Any well-painting source arriving is worth a re-apply — verticals load
      // under their own source, so keying this on the horizontals alone left a
      // vertical-only jump waiting for an unrelated tile event.
      const isWellSource = WELL_SOURCE_IDS.some(
        (sourceId) => sourceId === event?.sourceId
      );
      if (!isWellSource || !event?.isSourceLoaded) return;
      invalidate();
      applyAttentionWells();
    };

    mapbox.on("idle", applyAttentionWells);
    mapbox.on("sourcedata", onWellTiles);
    mapbox.on("style.load", invalidate);
    return () => {
      mapbox.off("idle", applyAttentionWells);
      mapbox.off("sourcedata", onWellTiles);
      mapbox.off("style.load", invalidate);
    };
  }, [mapboxRef, isMapInitialized, highlightWells.length, applyAttentionWells]);

  return {
    clearHoverHighlighting,
    clearHoverHighlightingDebounced
  };
}
